/*
  Copyright (c) 2018-present, Accuracy.
  All rights reserved.
*/
import {createStyles, Link, StyleRules, Theme} from "@material-ui/core";
import makeStyles from '@material-ui/core/styles/makeStyles';
import classNames from 'classnames';
import React, {memo} from 'react';
import {useTranslation} from "react-i18next";
import {asset} from "../../utils/asset";

//----------------------------------------------------------------------------------------------------------------------
//- Styles
//----------------------------------------------------------------------------------------------------------------------
export type ExposClassKey = 'root' | 'heading' | 'intro' | 'row' | 'thumb' | 'content' | 'title' | 'subtitle' | 'desc' | 'linkLine' | 'logo';

const useStyles = makeStyles<Theme, {}, ExposClassKey>(
  (): StyleRules<ExposClassKey> =>
    createStyles({
      root: {
        padding: 'clamp(48px, 10vh, 120px) clamp(20px, 4vw, 56px) clamp(60px, 10vh, 120px)'
      },
      heading: {
        margin: '0 0 clamp(20px, 3vh, 32px)',
        fontFamily: 'var(--font-display)',
        fontSize: 'clamp(30px, 4vw, 56px)',
        letterSpacing: '-.015em'
      },
      intro: {
        margin: '0 0 clamp(28px, 5vh, 48px)',
        maxWidth: '62ch',
        fontSize: '15px',
        lineHeight: 1.85,
        color: 'var(--color-text-body)'
      },
      row: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: 'clamp(20px, 3vw, 40px)',
        padding: 'clamp(24px, 4vh, 40px) 0',
        borderTop: '1px solid var(--color-rule)',
        '&:last-child': {
          borderBottom: '1px solid var(--color-rule)'
        }
      },
      thumb: {
        flex: '0 0 auto',
        width: '140px',
        height: '210px',
        objectFit: 'cover',
        background: 'var(--color-surface)'
      },
      content: {
        flex: '1 1 320px',
        display: 'flex',
        flexDirection: 'column',
        gap: '8px'
      },
      title: {
        margin: 0,
        fontFamily: 'var(--font-display)',
        fontSize: 'clamp(20px, 2.4vw, 28px)',
        lineHeight: 1.2,
        color: 'var(--color-text)'
      },
      subtitle: {
        margin: 0,
        fontSize: '12.5px',
        letterSpacing: '.1em',
        textTransform: 'uppercase',
        color: 'var(--color-text-faint)'
      },
      desc: {
        margin: '6px 0 0',
        fontSize: '14px',
        lineHeight: 1.7,
        color: 'var(--color-text-body)'
      },
      linkLine: {
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        marginTop: '4px'
      },
      logo: {
        height: '22px'
      }
    }),
  {
    name: 'Expos'
  });

//----------------------------------------------------------------------------------------------------------------------
//- Component
//----------------------------------------------------------------------------------------------------------------------
export type ExposProps = {
  className?: string;
};

export default memo(({className}: ExposProps) => {
  // hooks
  const classes = useStyles({});
  const {t} = useTranslation('common');

  return (
    <div className={classNames(classes.root, className)}>
      <h2 className={classes.heading}>{t('topBarMenu.expositions')}</h2>
      <p className={classes.intro}>{t('expos.description')}</p>

      <div className={classes.row}>
        <img className={classes.thumb} src={asset('files/img/Expos/salon-de-la-photo-2018.jpg')} alt="Salon de la photographie 2018"/>
        <div className={classes.content}>
          <h3 className={classes.title}>Salon de photographie (November, 2018 - Paris)</h3>
          <span className={classes.subtitle}>Coin des photographes</span>
          <p className={classes.desc}>Exposé certaines oeuvres au salon de ...</p>
          <div className={classes.linkLine}>
            <Link href={'https://www.lesalondelaphoto.com/'}>Site du salon de la photo</Link>
          </div>
        </div>
      </div>

      <div className={classes.row}>
        <img className={classes.thumb} src={asset('files/img/Expos/St_Gaudens.jpg')} alt="Exposition Saint-Gaudens 2017"/>
        <div className={classes.content}>
          <h3 className={classes.title}>Exposition (November, 2017 - Saint Gaudens)</h3>
          <span className={classes.subtitle}>Office du Tourisme</span>
          <p className={classes.desc}>Exposé certaines oeuvres au ...</p>
          <div className={classes.linkLine}>
            <img className={classes.logo} src={asset('files/img/logo_depeche.png')} alt="La Dépêche"/>
            <Link href={'https://www.ladepeche.fr/article/2017/11/03/2677408-noir-blanc-comme-aime-pierre-castay.html'}>Article La Depeche: "Du noir et blanc comme on l'aime"</Link>
          </div>
          <div className={classes.linkLine}>
            <img className={classes.logo} src={asset('files/img/logo_petit_journal.png')} alt="Le Petit Journal"/>
            <Link href={'https://www.lepetitjournal.net/31c-comminges/2017/11/06/inauguration-de-lexposition-photos-de-pierre-castay'}>Article Le Petit Journal: "Inauguration de l'exposition"</Link>
          </div>
        </div>
      </div>

      <div className={classes.row}>
        <img className={classes.thumb} src={asset('files/img/Expos/Tournefeuille.jpg')} alt="Exposition Tournefeuille 2016"/>
        <div className={classes.content}>
          <h3 className={classes.title}>Exposition (October, 2016 - Tournefeuille)</h3>
          <span className={classes.subtitle}>Maison Pahin</span>
          <p className={classes.desc}>Exposé certaines oeuvres au salon de ...</p>
          <div className={classes.linkLine}>
            <img className={classes.logo} src={asset('files/img/logo_depeche.png')} alt="La Dépêche"/>
            <Link href={'https://www.ladepeche.fr/article/2016/10/21/2443511-la-vie-en-noir-et-blanc-de-pierre-castay.html'}>Article La Depeche: "La vie en noir et blanc"</Link>
          </div>
        </div>
      </div>
    </div>
  );
});
